import { denormalize } from 'normalizr';
import reduceReducers from 'reduce-reducers';

import { createEntitiesReducer } from 'modules/common/redux';

import { PAGE_SUCCESS } from 'store/app/actions';

import { types } from './actions';
import {
  fastenerBomItemSchema,
  unresolvedFastenerBomItemSchema,
} from './entities';

const initialState = {
  availableDistributors: [],
  availableDistributorsFailed: false,
  availableDistributorsIsFetching: false,
  entities: {},
  fastenerBomIds: [],
  unresolvedFastenerBomIds: [],
  makeQuantities: [1, 5, 10, 50],
  preferredDistributorIds: [],
  selectedDistributorIds: [],
  selectedBomItemId: null,
  selectedUnresolvedBomItemId: null,
  selectedMakeQuantityIndex: 0,
  distributorsDetail: [],
  distributorsBOM: [],
  isResolutionDrawerOpen: false,
};

const entitiesReducer = createEntitiesReducer(
  ['fastenerBomItems', 'unresolvedFastenerBomItems'],
  [
    types.FASTENER_BOM_SET,
    types.FASTENER_BOM_ADD_ITEM_SUCCESS,
    types.UNRESOLVED_FASTENER_BOM_SET,
  ],
);

const reducer = (state = initialState, action) => {
  const { type, payload } = action;

  switch (type) {
    case PAGE_SUCCESS:
      return {
        ...initialState,
      };
    case types.FASTENER_BOM_ADD_ITEM_SUCCESS:
      return {
        ...state,
        fastenerBomIds: Array.from(
          new Set([...state.fastenerBomIds, payload.result]),
        ),
      };
    case types.FASTENER_BOM_REMOVE_ITEM:
      return {
        ...state,
        fastenerBomIds: state.fastenerBomIds.filter(
          (fastenerId) => fastenerId != payload.productId,
        ),
      };
    case types.FASTENER_BOM_SET:
      return {
        ...state,
        fastenerBomIds: payload.result,
      };
    case types.UNRESOLVED_FASTENER_BOM_SET:
      return {
        ...state,
        unresolvedFastenerBomIds: payload.result,
      };
    case types.FETCH_AVAILABLE_DISTRIBUTORS_ERROR:
      return {
        ...state,
        availableDistributorsFailed: true,
        availableDistributorsIsFetching: false,
      };
    case types.FETCH_AVAILABLE_DISTRIBUTORS_REQUEST:
      return {
        ...state,
        availableDistributorsFailed: false,
        availableDistributorsIsFetching: true,
      };
    case types.FETCH_AVAILABLE_DISTRIBUTORS_SUCCESS:
      return {
        ...state,
        availableDistributors: payload.distributors,
        availableDistributorsFailed: false,
        availableDistributorsIsFetching: false,
      };
    case types.SELECTED_BOM_ITEM_SET:
      return {
        ...state,
        selectedBomItemId: payload.productId,
      };
    case types.SELECTED_DISTRIBUTORS_ADD:
      return {
        ...state,
        selectedDistributorIds: [
          ...state.selectedDistributorIds,
          payload.distributorId,
        ],
      };
    case types.SELECTED_DISTRIBUTORS_REMOVE:
      return {
        ...state,
        selectedDistributorIds: state.selectedDistributorIds.filter(
          (id) => id != payload.distributorId,
        ),
      };
    case types.SELECTED_DISTRIBUTORS_SET:
      return {
        ...state,
        selectedDistributorIds: payload.distributorId,
      };
    case types.SELECTED_DISTRIBUTORS_SET_INITIAL:
      return {
        ...state,
        selectedDistributorIds: payload.distributorId,
        preferredDistributorIds: payload.distributorId,
      };
    case types.MAKE_QUANTITIES_SELECT:
      return {
        ...state,
        selectedMakeQuantityIndex: payload.index,
      };
    case types.MAKE_QUANTITIES_UPDATE:
      return {
        ...state,
        makeQuantities: [...payload.makeQuantities],
        selectedMakeQuantityIndex: 0,
      };
    case types.SELECTED_DISTRIBUTORS_ADD_DETAIL:
      return {
        ...state,
        distributorsDetail: [
          ...state.distributorsDetail,
          payload.distributorId,
        ],
      };
    case types.SELECTED_DISTRIBUTORS_REMOVE_DETAIL:
      return {
        ...state,
        distributorsDetail: state.distributorsDetail.filter(
          (id) => id !== payload.distributorId,
        ),
      };

    case types.SELECTED_DISTRIBUTORS_ADD_DETAIL_BULK:
      return {
        ...state,
        distributorsDetail: payload.distributorIds,
      };
    case types.SELECTED_DISTRIBUTORS_ADD_BOM_DRAWER:
      return {
        ...state,
        distributorsBOM: [...state.distributorsBOM, payload.distributorId],
      };
    case types.SELECTED_DISTRIBUTORS_REMOVE_BOM_DRAWER:
      return {
        ...state,
        distributorsBOM: state.distributorsBOM.filter(
          (id) => id !== payload.distributorId,
        ),
      };

    case types.SELECTED_DISTRIBUTORS_ADD_BULK_BOM_DRAWER:
      return {
        ...state,
        distributorsBOM: payload.distributorIds,
      };

    case types.RESOLVE_MATCH_REQUEST:
      return {
        ...state,
        selectedUnresolvedBomItemId: payload.partUuid,
        isResolutionDrawerOpen: true,
      };
    case types.RESOLVE_MATCH_SUCCESS:
      return {
        ...state,
        unresolvedFastenerBomIds: state.unresolvedFastenerBomIds.filter(
          (id) => id !== state.selectedUnresolvedBomItemId,
        ),
        isResolutionDrawerOpen: false,
      };
    case types.RESOLVE_MATCH_CANCEL:
      return {
        ...state,
        selectedUnresolvedBomItemId: null,
        isResolutionDrawerOpen: false,
      };

    default:
      return state;
  }
};

export default reduceReducers(entitiesReducer, reducer, initialState);

const getScope = (state) => state.modules.viewerPage.app.pennApp;

export const getAvailableDistributors = (state) =>
  getScope(state).availableDistributors;

export const getAllAvailableDistributorIds = (state) =>
  getAvailableDistributors(state).map(({ id }) => id);

export const getBomItemByProductId = (state, productId) =>
  denormalize(productId, fastenerBomItemSchema, getScope(state).entities);

export const getFastenerBom = (state) =>
  denormalize(
    getScope(state).fastenerBomIds,
    [fastenerBomItemSchema],
    getScope(state).entities,
  );

export const getUnresolvedFastenerBom = (state) =>
  denormalize(
    getScope(state).unresolvedFastenerBomIds,
    [unresolvedFastenerBomItemSchema],
    getScope(state).entities,
  );

export const getIsFastenerBomEmpty = (state) =>
  getScope(state).fastenerBomIds.length == 0;

export const getIsBomItemSelected = (state) =>
  Boolean(getScope(state).selectedBomItemId);

export const getIsProductInBom = (state, productId) =>
  getScope(state).fastenerBomIds.includes(productId);

export const getSelectedBomItem = (state) =>
  denormalize(
    getScope(state).selectedBomItemId,
    fastenerBomItemSchema,
    getScope(state).entities,
  );

export const getSelectedUnresolvedBomItem = (state) =>
  denormalize(
    getScope(state).selectedUnresolvedBomItemId,
    unresolvedFastenerBomItemSchema,
    getScope(state).entities,
  );

export const failedToLoadAvailableDistributors = (state) =>
  getScope(state).availableDistributorsFailed;

export const isFetchingAvailableDistributors = (state) =>
  getScope(state).availableDistributorsIsFetching;

export const getSelectedDistributorIds = (state) =>
  getScope(state).selectedDistributorIds;

export const isDistributorSelected = (state, distributorId) =>
  getSelectedDistributorIds(state).includes(distributorId);

export const getMakeQuantities = (state) => getScope(state).makeQuantities;

export const getSelectedMakeQuantityIndex = (state) =>
  getScope(state).selectedMakeQuantityIndex;

export const getSelectedMakeQuantity = (state) =>
  getMakeQuantities(state)[getSelectedMakeQuantityIndex(state)];

export const getSelectedDistributorIdsDetail = (state) =>
  getScope(state).distributorsDetail;

export const isDistributorSelectedDetail = (state, distributorId) =>
  getSelectedDistributorIdsDetail(state).includes(distributorId);

export const getSelectedDistributorIdsBOM = (state) =>
  getScope(state).distributorsBOM;

export const isDistributorSelectedBOM = (state, distributorId) =>
  getSelectedDistributorIdsBOM(state).includes(distributorId);

export const getSelectedDistributor = (state) => {
  return getAvailableDistributors(state).find(({ id }) =>
    getSelectedDistributorIds(state).includes(id),
  );
};

export const getIsResolutionDrawerOpen = (state) =>
  getScope(state).isResolutionDrawerOpen;

export const getPreferredDistributorIds = (state) =>
  getScope(state).preferredDistributorIds;
